import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import Quill from 'quill';
import { inject } from '@ember/service';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { schedule } from '@ember/runloop';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div\n  id={{this.id}}\n  ...attributes\n  {{did-insert this.initQuill}}\n  {{did-update this.updateQuill enabled=@enabled delta=@delta}}\n></div>");

var _dec, _class, _descriptor;
let QuillEditorComponent = (_dec = inject('quill'), _class = class QuillEditorComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "quillService", _descriptor, this);
    this.quill = null;
    this.value = null;
    // Last @delta argument applied via setContents; used to detect real
    // @delta changes in updateQuill without clobbering user edits.
    this._appliedDelta = null;
  }
  get id() {
    return this.args.id ?? guidFor(this);
  }
  get name() {
    return this.args.name ?? this.id;
  }
  get modules() {
    return {
      ...(this.args.modules ?? {}),
      ...(this.args._toolbar ? {
        toolbar: this.args._toolbar
      } : {})
    };
  }
  initQuill(element) {
    this.quill = new Quill(element, {
      bounds: this.args.bounds,
      debug: this.args.debug ?? 'warn',
      formats: this.args.formats,
      modules: this.modules,
      placeholder: this.args.placeholder,
      readOnly: this.args.readOnly ?? false,
      scrollingContainer: this.args.scrollingContainer ?? null,
      theme: this.args.theme
    });
    this.quill.enable(Boolean(this.args.enabled ?? true));
    if (true === this.args.focused) {
      this.quill.focus();
    }
    if (this.args.delta) {
      this._appliedDelta = this.args.delta;
      this.value = this.quill.setContents(this.args.delta);
      this.doText();
    } else if (this.args.text) {
      this.value = this.quill.setText(this.args.text);
      this.doChange();
    } else {
      this.value = this.quill.getContents();
    }

    // emit the length and words on start-up
    this.doLength();
    this.doWords();

    /** Native Quill Events */
    this.quill.on('text-change', this.doTextChange);
    this.quill.on('selection-change', this.doSelectionChange);
    this.quill.on('editor-change', this.doEditorChange);

    /** Custom Events */
    this.quill.on('text-change', this.doLength);
    this.quill.on('text-change', this.doWords);
    this.quill.on('text-change', this.doChange);
    this.quill.on('text-change', this.doText);
    this.quillService.register(this.name, this.quill);
    if (this.args.onInit) {
      this.args.onInit(this.quill);
    }
  }
  updateQuill(el, args, {
    enabled,
    delta
  }) {
    if (this.quill) {
      this.quill.enable(enabled ?? true);

      // Only re-apply content when the @delta argument itself changed:
      // comparing against this.value (the Delta returned by set/getContents)
      // was always !== the incoming arg, so an @enabled toggle without @delta
      // wiped the editor via setContents(undefined) and reverted user edits.
      if (delta && delta !== this._appliedDelta) {
        this._appliedDelta = delta;
        this.value = this.quill.setContents(delta, Quill.sources.SILENT);
      }
    }
  }
  doTextChange(delta, oldDelta, source) {
    if (this.args.onTextChange) {
      schedule('actions', this, this._handleEvent, 'onTextChange', delta, oldDelta, source);
    }
  }
  doSelectionChange(range, oldRange, source) {
    if (this.args.onSelectionChange) {
      schedule('actions', this, this._handleEvent, 'onSelectionChange', range, oldRange, source);
    }
  }
  doEditorChange(eventName, ...args) {
    if (this.args.onEditorChange) {
      schedule('actions', this, this._handleEvent, 'onEditorChange', eventName, ...args);
    }
  }
  doLength() {
    if (this.quill && this.args.onLength) {
      schedule('actions', this, this._handleEvent, 'onLength', this.quill.getLength());
    }
  }
  doChange() {
    if (this.quill && this.args.onChange) {
      this.value = this.quill.getContents();
      schedule('actions', this, this._handleEvent, 'onChange', this.value);
    }
  }
  doText() {
    if (this.quill && this.args.onText) {
      schedule('actions', this, this._handleEvent, 'onText', this.quill.getText());
    }
  }
  doWords() {
    if (this.quill && this.args.onWords) {
      const text = this.quill.getText();
      schedule('actions', this, this._handleEvent, 'onWords', this._calculateWords(text));
    }
  }
  willDestroy() {
    super.willDestroy(...arguments);
    if (this.quill) {
      /** Native Quill Events */
      this.quill.off('text-change', this.doTextChange);
      this.quill.off('selection-change', this.doSelectionChange);
      this.quill.off('editor-change', this.doEditorChange);

      /** Custom Events */
      this.quill.off('text-change', this.doLength);
      this.quill.off('text-change', this.doWords);
      this.quill.off('text-change', this.doChange);
      this.quill.off('text-change', this.doText);
    }
    this.quillService.deregister(this.name, this.quill);
    this.quill = null;
    this.value = null;
  }
  _handleEvent(callback, ...args) {
    if (!this.isDestroying && !this.isDestroyed && this.args[callback]) {
      this.args[callback](...args);
    }
  }
  _calculateWords(text) {
    text = text.trim();
    return text.length > 0 ? text.split(/\s+/).length : 0;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "quillService", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "initQuill", [action], Object.getOwnPropertyDescriptor(_class.prototype, "initQuill"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateQuill", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateQuill"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "doTextChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "doTextChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "doSelectionChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "doSelectionChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "doEditorChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "doEditorChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "doLength", [action], Object.getOwnPropertyDescriptor(_class.prototype, "doLength"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "doChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "doChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "doText", [action], Object.getOwnPropertyDescriptor(_class.prototype, "doText"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "doWords", [action], Object.getOwnPropertyDescriptor(_class.prototype, "doWords"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, QuillEditorComponent);

export { QuillEditorComponent as default };
//# sourceMappingURL=quill-editor.js.map
